import type { Tile } from '../../engine/mahjongTiles';

/**
 * Jade mahjong tiles (user, 2026-09-27, mockup H): an ivory face on a jade
 * body, jade backs. Every face has a thin strip across the top holding the
 * suit number or wind letter (dragons blank), and the art below it at full
 * size, so the mark never moves or overlaps the art.
 */
const CN = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];
const HONORS = ['東', '南', '西', '北', '中', '發', ''];
const WIND_LETTER = ['E', 'S', 'W', 'N'];
const NAMES = ['East', 'South', 'West', 'North', 'Red dragon', 'Green dragon', 'White dragon'];
/** The flowers and seasons (34-41): their character over a small sprig,
 *  numbered 1-4 in the strip (1 is East's, 2 South's, ...). */
const FLOWERS = ['梅', '蘭', '菊', '竹', '春', '夏', '秋', '冬'];
const FLOWER_NAMES = ['Plum', 'Orchid', 'Chrysanthemum', 'Bamboo', 'Spring', 'Summer', 'Autumn', 'Winter'];
const FLOWER_INK = ['#c2185b', '#7b3fa0', '#c77d00', '#2e8b57', '#2e8b57', '#b3261e', '#c77d00', '#1d6fb8'];

const DOTS: Record<number, [number, number, number][]> = {
  1: [[15, 20, 8]],
  2: [[15, 11, 4.5], [15, 29, 4.5]],
  3: [[8, 9, 4], [15, 20, 4], [22, 31, 4]],
  4: [[9, 12, 4], [21, 12, 4], [9, 28, 4], [21, 28, 4]],
  5: [[8, 10, 3.6], [22, 10, 3.6], [15, 20, 3.6], [8, 30, 3.6], [22, 30, 3.6]],
  6: [[9, 9, 3.3], [21, 9, 3.3], [9, 20, 3.3], [21, 20, 3.3], [9, 31, 3.3], [21, 31, 3.3]],
  7: [[7, 7, 2.9], [15, 11, 2.9], [23, 15, 2.9], [9, 24, 2.9], [21, 24, 2.9], [9, 33, 2.9], [21, 33, 2.9]],
  8: [[9, 6, 2.8], [21, 6, 2.8], [9, 15, 2.8], [21, 15, 2.8], [9, 25, 2.8], [21, 25, 2.8], [9, 34, 2.8], [21, 34, 2.8]],
  9: [[7, 8, 2.8], [15, 8, 2.8], [23, 8, 2.8], [7, 20, 2.8], [15, 20, 2.8], [23, 20, 2.8], [7, 32, 2.8], [15, 32, 2.8], [23, 32, 2.8]],
};
const DOT_COLORS: Record<number, string[]> = { 1: ['#c0392b'], 2: ['#1d6fb8', '#2e8b57'], 3: ['#1d6fb8', '#c0392b', '#2e8b57'] };
const dotColor = (n: number, i: number) => {
  const c = DOT_COLORS[n] ?? ['#1d6fb8', '#2e8b57', '#c0392b'];
  return c[i % c.length]!;
};
const BAM: Record<number, [number, number, number][]> = {
  2: [[15, 5, 13], [15, 22, 13]],
  3: [[15, 5, 13], [9, 22, 13], [21, 22, 13]],
  4: [[9, 5, 13], [21, 5, 13], [9, 22, 13], [21, 22, 13]],
  5: [[8, 5, 13], [22, 5, 13], [15, 13, 13], [8, 22, 13], [22, 22, 13]],
  6: [[7, 5, 13], [15, 5, 13], [23, 5, 13], [7, 22, 13], [15, 22, 13], [23, 22, 13]],
  7: [[15, 3, 10], [7, 15, 10], [15, 15, 10], [23, 15, 10], [7, 27, 10], [15, 27, 10], [23, 27, 10]],
  8: [[6, 5, 13], [12, 5, 13], [18, 5, 13], [24, 5, 13], [6, 22, 13], [12, 22, 13], [18, 22, 13], [24, 22, 13]],
  9: [[7, 3, 10], [15, 3, 10], [23, 3, 10], [7, 15, 10], [15, 15, 10], [23, 15, 10], [7, 27, 10], [15, 27, 10], [23, 27, 10]],
};

export function tileName(t: Tile): string {
  if (t >= 34) return `${FLOWER_NAMES[t - 34]} (${t < 38 ? 'flower' : 'season'} ${((t - 34) % 4) + 1})`;
  return t >= 27 ? NAMES[t - 27]! : `${(t % 9) + 1} ${['Characters', 'Dots', 'Bamboo'][Math.floor(t / 9)]}`;
}

function Art({ t }: { t: Tile }) {
  if (t >= 34) {
    const ink = FLOWER_INK[t - 34]!;
    return (
      <>
        <text x="15" y="22" fontSize="17" textAnchor="middle" className="mj-cjk" fill={ink}>{FLOWERS[t - 34]}</text>
        <path d="M9 34 C12 29 18 29 21 34" fill="none" stroke="#2e8b57" strokeWidth="1.4" />
        <circle cx="10" cy="32" r="1.6" fill={ink} />
        <circle cx="20" cy="32" r="1.6" fill={ink} />
      </>
    );
  }
  const suit = t >= 27 ? 3 : Math.floor(t / 9);
  const rank = (t % 9) + 1;
  if (suit === 0) {
    return (
      <>
        <text x="15" y="16" fontSize="13" textAnchor="middle" className="mj-cjk" fill="#1c1a17">{CN[rank - 1]}</text>
        <text x="15" y="34" fontSize="15" textAnchor="middle" className="mj-cjk" fill="#b3261e">萬</text>
      </>
    );
  }
  if (suit === 1) {
    return (
      <>
        {DOTS[rank]!.map(([x, y, r], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r={r} fill="none" stroke={dotColor(rank, i)} strokeWidth={r > 5 ? 2.4 : 1.6} />
            <circle cx={x} cy={y} r={r * 0.38} fill={dotColor(rank, i)} />
          </g>
        ))}
      </>
    );
  }
  if (suit === 2) {
    if (rank === 1) {
      return (
        <>
          <path d="M8 26 C6 16 12 8 18 9 C23 10 24 15 21 18 C26 18 27 24 23 27 L15 30 Z" fill="#2e8b57" />
          <circle cx="18" cy="12" r="1.6" fill="#1c1a17" />
          <path d="M21 11 L25 10 L22 13 Z" fill="#c0392b" />
          <path d="M11 27 L7 35 M14 29 L12 36" stroke="#b3261e" strokeWidth="1.6" />
        </>
      );
    }
    const red = (i: number) => (rank === 7 && i === 0) || (rank === 5 && i === 2) || (rank === 9 && i % 3 === 1);
    return (
      <>
        {BAM[rank]!.map(([x, y, l], i) => (
          <g key={i}>
            <rect x={x - 2} y={y} width="4" height={l} rx="2" fill={red(i) ? '#c0392b' : '#2e8b57'} />
            <rect x={x - 2.4} y={y + l / 2 - 0.7} width="4.8" height="1.4" fill="#1b5e3a" />
          </g>
        ))}
      </>
    );
  }
  const h = t - 27;
  if (h === 6) {
    return (
      <>
        <rect x="6" y="7" width="18" height="26" rx="2" fill="none" stroke="#1d6fb8" strokeWidth="2.2" />
        <rect x="9" y="10" width="12" height="20" rx="1" fill="none" stroke="#1d6fb8" strokeWidth="1" />
      </>
    );
  }
  return (
    <text x="15" y="27" fontSize="19" textAnchor="middle" className="mj-cjk" fill={h === 4 ? '#b3261e' : h === 5 ? '#2e8b57' : '#1c1a17'}>
      {HONORS[h]}
    </text>
  );
}

export interface MjTileProps {
  t: Tile;
  /** Width in px; everything else scales from it. */
  w: number;
  className?: string;
  onClick?: () => void;
  label?: string;
}

export function MjTile({ t, w, className = '', onClick, label }: MjTileProps) {
  const suit = t >= 27 ? 3 : Math.floor(t / 9);
  const mark = suit < 3 ? String((t % 9) + 1) : t <= 30 ? WIND_LETTER[t - 27] : t >= 34 ? String(((t - 34) % 4) + 1) : '';
  const body = (
    <>
      <span className={`mj-mark${suit === 3 ? ' wind' : ''}`}>{mark}</span>
      <span className="mj-strip" />
      <svg viewBox="0 0 30 40" aria-hidden="true">
        <Art t={t} />
      </svg>
    </>
  );
  const style = { '--w': `${w}px` } as React.CSSProperties;
  if (onClick) {
    return (
      <button className={`mj-tile ${className}`} style={style} onClick={onClick} aria-label={label ?? tileName(t)}>
        {body}
      </button>
    );
  }
  return (
    <span className={`mj-tile ${className}`} style={style} role="img" aria-label={label ?? tileName(t)}>
      {body}
    </span>
  );
}

export function MjBack({ w }: { w: number }) {
  return <span className="mj-tile back" style={{ '--w': `${w}px` } as React.CSSProperties} aria-hidden="true" />;
}
